import {
  Button,
  Col,
  DatePicker,
  Form,
  Layout,
  message,
  Modal,
  Row,
  Select,
  Upload,
  Table,
  Tag,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Inbox, Link, UploadIcon } from "lucide-react";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatNumbers,
  formatDate,
  formatString,
  handleExportToExcel,
  handleExportToPDF,
} from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useTableData } from "../../Utils/useTableData";

dayjs.extend(customParseFormat);

const { Dragger } = Upload;
const { Option } = Select;

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "billed", label: "Billed" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

function renderStatusTag(status: any) {
  const formattedStatus = String(status || "").toLowerCase();
  let color = "default";
  let label = status || "Unknown";

  switch (formattedStatus) {
    case "pending":
      color = "orange";
      label = "Pending";
      break;
    case "billed":
      color = "blue";
      label = "Billed";
      break;
    case "completed":
    case "paid":
      color = "green";
      label = formattedStatus === "paid" ? "Paid" : "Completed";
      break;
    case "cancelled":
      color = "red";
      label = "Cancelled";
      break;
    default:
      break;
  }

  return <Tag color={color}>{label}</Tag>;
}

/** Normalize Excel datetime strings for API (old UAT expects m/d/Y H:i:s). */
function normalizeExcelDateTime(value: any): string | null {
  if (value == null || String(value).trim() === "") return null;
  const raw = String(value).trim();

  // Excel serial number (e.g. 45842.4375)
  if (/^\d+(\.\d+)?$/.test(raw)) {
    const parsed = dayjs("1899-12-30").add(Number(raw), "day");
    if (parsed.isValid()) return parsed.format("MM/DD/YYYY HH:mm:ss");
  }

  const cleaned = raw.replace(/\s*(AM|PM)$/i, "").trim();
  const formats = [
    "M/D/YY H:mm",
    "M/D/YY HH:mm",
    "M/D/YY H:mm:ss",
    "M/D/YY HH:mm:ss",
    "M/D/YYYY H:mm",
    "M/D/YYYY HH:mm",
    "M/D/YYYY H:mm:ss",
    "M/D/YYYY HH:mm:ss",
    "MM/DD/YYYY HH:mm:ss",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD HH:mm",
    "M/D/YY",
    "M/D/YYYY",
    "MM/DD/YYYY",
    "YYYY-MM-DD",
  ];

  for (const format of formats) {
    const parsed = dayjs(cleaned, format, true);
    if (parsed.isValid()) return parsed.format("MM/DD/YYYY HH:mm:ss");
  }

  const loose = dayjs(raw);
  return loose.isValid() ? loose.format("MM/DD/YYYY HH:mm:ss") : raw;
}

function normalizeExcelDate(value: any): string | null {
  const dt = normalizeExcelDateTime(value);
  if (!dt) return null;
  // Keep date-only as YYYY-MM-DD for date_sampling
  const parsed = dayjs(dt, "MM/DD/YYYY HH:mm:ss", true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
}

const DailySalesReport = () => {
  const BASE_URL = process.env.REACT_APP_LINK;
  const [loading, setLoading] = useState("");
  const [showModal, setShowModal] = useState("");
  const [form] = Form.useForm();
  const [selectedRow, setSelectedRow]: any = useState({});
  const [brands, setBrands]: any = useState([]);
  const [vehicleTypes, setVehicleTypes]: any = useState([]);

  const {
    dataSource: rawDataSource,
    dataSize,
    loading: tableLoading,
    filterConfig,
    setFilterConfig,
    refetch: fetchTableData,
    handleChangePage,
    handlePageSizeChange,
  } = useTableData<any>({
    endpoint: "daily_transactions/get",
    initialFilters: {
      vehicle_type_id: "",
      brand_id: "",
      status: "",
      date_from: "",
      date_to: "",
    },
    isFormData: true,
  });

  const dataSource = useMemo(
    () =>
      rawDataSource.map((item: any) => ({
        ...item,
        formatted_added_by: item.added_by_name
          ? `${item.added_by_name} on ${
              item.added_on
                ? dayjs(item.added_on).format("MMM DD, YYYY h:mm A")
                : ""
            }`
          : "",
      })),
    [rawDataSource]
  );

  function clearFields() {
    setSelectedRow({});
    form.resetFields();
  }

  const columns = [
    {
      title: "Transaction No.",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Transaction Date",
      dataIndex: "transaction_date",
      key: "transaction_date",
      render: (date: any) => formatDate(date, "MMM DD, YYYY h:mm A"),
    },
    { title: "Customer", 
      dataIndex: "customer_name", 
      key: "customer_name" 
    },
    { title: "Type", 
      dataIndex: "test_type",
      key: "test_type" 
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      align: "right" as any,
      render: (text: any) => formatNumbers(text),
    },
    {
      title: "Payment Amount",
      dataIndex: "payment_amount",
      key: "payment_amount",
      align: "right" as any,
      render: (text: any) => formatNumbers(text),
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatusTag,
    },
    {
      title: "Balance",
      dataIndex: "current_balance",
      key: "current_balance",
      align: "right" as any,
      render: (text: any) => formatNumbers(text),
    },
    {
      title: "Uploaded By",
      dataIndex: "added_by_name",
      key: "added_by_name",
    },
    // {
    //   title: "",
    //   key: "action",
    //   width: 10,
    //   render: (_: any, record: any) => (
    //     <Dropdown
    //       overlay={
    //         <Menu>
    //           <Menu.Item
    //             key="edit"
    //             icon={<Pen strokeWidth={1.5} size={15} />}
    //             onClick={() => navigateTo("/daily-sales-report/edit/" + record.id)}
    //           >
    //             Edit
    //           </Menu.Item>
    //           <Menu.Item
    //             key="view"
    //             icon={<Plus strokeWidth={1.5} size={15} />}
    //             onClick={() => navigateTo("/discounts/add/" + record.id)}
    //           >
    //             Add Discount
    //           </Menu.Item>
    //       }
    //       trigger={["click"]}
    //     >
    //       <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
    //     </Dropdown>
    //   ),
    // },
  ];

  const handleBeforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(`${file.name} exceeds the 2MB size limit.`);
    }
    return isLt2M ? false : Upload.LIST_IGNORE;
  };

  function handleFilterChange(value: any, field: string) {
    setFilterConfig((prev: any) => ({
      ...prev,
      [field]: value ?? "",
      page: 1,
    }));
  }

  function handleDateFilterChange(value: any, field: "date_from" | "date_to") {
    setFilterConfig((prev: any) => ({
      ...prev,
      [field]: value ? dayjs(value).format("YYYY-MM-DD") : "",
      page: 1,
    }));
  }

  function exportToExcel() {
    handleExportToExcel(dataSource, columns, "DailySalesReport");
  }

  function exportToPDF() {
    handleExportToPDF(
      dataSource,
      columns,
      "DailySalesReport.pdf",
      {
        title: "List of DailySales",
        content: `Generated on ${new Date().toLocaleString()}`,
      },
      `Total Records: ${dataSource.length}`
    );
  }

  function submitForm(data: any) {
    setLoading("submit");
    handleUploadDailySales(data);
  }

  function handleUploadDailySales(data: any) {
    const file = data?.attachment?.[0]?.originFileObj;
    if (!file) {
      message.error("No file uploaded.");
      setLoading("");
      return;
    }

    const extension = file.name.split(".").pop().toLowerCase();
    const allowedExtensions = ["xlsx", "xls", "csv"];

    if (!allowedExtensions.includes(extension)) {
      message.error("Invalid file type. Please upload xlsx, xls, or csv.");
      setLoading("");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      try {
        const fileData = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(fileData, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          raw: false,
          dateNF: "MM/DD/YYYY",
        });

        const arrayOfObj = jsonData
          .map((row: any) => ({
            rwt_no: row["RWT NUMBER"],
            payment_method: row["PAYMENT TYPE"],
            transaction_date: normalizeExcelDateTime(
              row["TRANSACTION DATE TIME"]
            ),
            customer_id: row["CLIENT ID"],
            sample_count: row["SAMPLE COUNTS"],
            date_sampling: normalizeExcelDate(row["DATE OF SAMPLING"]),
            package_test: row["PACKAGE TEST"],
            test_type: row["TEST TYPE"],
            special_instruction: row["SPECIAL INSTRUCTIONS"],
            sample_received_by:
              row["SAMPLER RECIEVED BY"] || row["SAMPLER RECEIVED BY"],
            processed_by: row["PROCESSED BY"],
            lab_received_by: row["LAB RECEIVED BY"],
            lab_analyzed_by: row["LAB ANALYZED BY 1"],
            lab_analyzed_by_2: row["LAB ANALYZED BY 2"],
            lab_remark: row["LAB REMARKS"],
            lab_reference: row["LAB REFERENCE"],
            head_chemist_approved_by: row["HEAD CHEMIST APPROVED BY"],
            printed_by: row["PRINTED BY"],
            total_amount: row["TOTAL AMOUNT"],
            total_tested_amount: row["TOTAL TESTED AMOUNT"],
            remaining_balance: row["REMAINING BALANCE"],
            payment_remarks: row["PAYMENT REMARKS"],
            paid_date: normalizeExcelDateTime(row["IS PAID DATE TIME"]),
            is_paid: row["IS PAID"],
            cancelled_by: row["IS CANCELLED BY"],
            ar_no: row["AR NO"],
            id_no: row["CLIENT ID"],
            owner_name: row["REGISTERED OWNER NAME"],
            contact_person: row["CONTACT PERSON NAME"],
            address: row["ADDRESS"],
            telephone_no: row["TELEPHONE_NO"],
            cellphone_no: row["CELLPHONE_NO"],
            email: row["EMAIL_ADDRESS"],
            fax_no: row["FAX_NO"],
            name: row["CLIENT COMPANY NAME"],
            tin:
              row["TIN"] ||
              row["TIN NO"] ||
              row["TIN NUMBER"] ||
              row["CLIENT TIN"] ||
              row["TIN_NO"],
          }))
          // Skip guide/sample rows (e.g. vertical SAMPLE block) that have no real transaction data
          .filter((row: any) => {
            const hasValue = (v: any) =>
              v != null && String(v).trim() !== "";
            return (
              hasValue(row.rwt_no) ||
              hasValue(row.customer_id) ||
              hasValue(row.transaction_date)
            );
          });

        if (arrayOfObj.length === 0) {
          setLoading("");
          message.error(
            "No valid transaction rows found. Put data under the header columns (not in the SAMPLE guide)."
          );
          return;
        }

        processDailySales(arrayOfObj);
      } catch (error) {
        setLoading("");
        message.error("Failed to parse Excel file.");
      }
    };

    reader.readAsArrayBuffer(file);
  }

  function chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  async function processDailySales(arrayOfObj: any[]) {
    const chunkedArray = chunkArray(arrayOfObj, 150);
    const results: any[] = [];

    try {
      for (const chunk of chunkedArray) {
        const result = await sendDailySales(chunk);
        results.push(result);
      }

      const hasError = results.some((res) => res?.error);
      if (hasError) {
        const firstError = results.find((res) => res?.error);
        message.error(
          firstError?.error?.messages?.error_messages?.[0] || "Upload failed."
        );
      }
      // else {
      //   message.success("Successfully uploaded Excel file.");
      // }
    } catch (error) {
      message.error("Error uploading Excel file.");
    } finally {
      setLoading("");
    }
  }

  async function sendDailySales(arrayData: any[]) {
    const response = await postRequest(
      `daily_transactions/${showModal}`,
      { details: arrayData },
      false
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        fetchTableData();
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${showModal} daily sales.`
      );
    }
  }

  async function fetchBrands() {
    setBrands([]);
    setLoading("fetch");
    var response = await getRequest(`brands/get`, {}, true);
    if (response.success) {
      var data = response.data.data.map((info: any) => ({
        ...info,
        brand_id: info.id,
        brand_name: info.name,
      }));
      setBrands(data);
    }
    setLoading("");
  }

  async function fetchVehicleTypes() {
    setVehicleTypes([]);
    setLoading("fetch");
    var response = await getRequest(`vehicle_types/get`, {}, true);
    if (response.success) {
      var data = response.data.data.map((info: any) => ({
        ...info,
        vehicle_type_id: info.id,
        vehicle_name: info.name,
      }));
      setVehicleTypes(data);
    }
    setLoading("");
  }

  useEffect(() => {
    fetchBrands();
    fetchVehicleTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Daily Sales Report"
              withDateRangeFilter={false}
              withCustomButton={true}
              customBtnText="Upload Daily Sales"
              customIcon={<UploadIcon size={16} />}
              onCreate={() => setShowModal("create")}
              withExportDropdownButton={true}
              disableExportButton={dataSource?.length === 0}
              onExportToExcel={() => exportToExcel()}
              onExportToPDF={() => exportToPDF()}
              withSearchBtn={true}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({
                  ...prev,
                  search: value ?? "",
                  page: 1,
                }))
              }
            />
          </Col>
          <Row gutter={[16, 16]} align="middle" className="mb-3">
            <Col>
              <span>Date From: </span>
              <DatePicker
                value={filterConfig.date_from ? dayjs(filterConfig.date_from) : null}
                format="YYYY-MM-DD"
                allowClear
                onChange={(value: any) => handleDateFilterChange(value, "date_from")}
              />
            </Col>

            <Col>
              <span>Date To: </span>
              <DatePicker
                value={filterConfig.date_to ? dayjs(filterConfig.date_to) : null}
                format="YYYY-MM-DD"
                allowClear
                onChange={(value: any) => handleDateFilterChange(value, "date_to")}
              />
            </Col>

            <Col>
              <span>Status: </span>
              <Select
                placeholder="Select Status"
                value={filterConfig.status || undefined}
                onChange={(value) => handleFilterChange(value, "status")}
                style={{ width: 160 }}
                allowClear
              >
                {STATUS_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="custom-table min-w-[800px]">
                <Table
                  bordered
                  dataSource={dataSource}
                  columns={columns}
                  rowKey="id"
                  loading={tableLoading}
                  size="small"
                  pagination={{
                    current: filterConfig.page,
                    total: dataSize,
                    showSizeChanger: true,
                    onChange: (page: number) => handleChangePage(page),
                    onShowSizeChange: (_: number, size: number) => handlePageSizeChange(size),
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Layout>
      <Modal
        open={showModal === "create"}
        title={`Upload Daily Sales`}
        centered
        footer={null}
        onCancel={() => {
          setShowModal("");
          clearFields();
        }}
        width={800}
        maskClosable={false}
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          <Form form={form} onFinish={submitForm} layout="vertical">
            <Row className="w-full" gutter={[10, 10]}>
              <Col xs={24} className="mt-5">
                <Form.Item
                  name="attachment"
                  valuePropName="fileList"
                  getValueFromEvent={(e) =>
                    Array.isArray(e) ? e : e?.fileList
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please upload a file.",
                    },
                  ]}
                >
                  <Dragger
                    name="file"
                    multiple={false}
                    beforeUpload={handleBeforeUpload}
                    accept=".xlsx,.xls,.csv"
                    maxCount={1}
                  >
                    <p className="ant-upload-drag-icon flex justify-center items-center text-3xl">
                      <Inbox />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag file to this area to upload
                    </p>
                    <p className="ant-upload-hint">
                      Only single file is allowed.
                    </p>
                  </Dragger>
                </Form.Item>
              </Col>
              <Col lg={24} className="m-0 p-0">
                <a
                  className="text-hyperlinkColor underline inline-flex items-center gap-1"
                  href={`${BASE_URL}/assets/Cebu-Agualab-Add-DSR-TemplateV4.xlsx`}
                  download="Cebu-Agualab-Add-DSR-TemplateV4.xlsx"
                >
                  <Link size={16} />
                  Download Excel Template
                </a>
              </Col>
              <Col lg={24} className="flex justify-end gap-3">
                <Form.Item>
                  <Button
                    onClick={() => {
                      setShowModal("");
                      clearFields();
                    }}
                  >
                    Cancel
                  </Button>
                </Form.Item>
                <Form.Item>
                  <Button
                    htmlType="submit"
                    type="primary"
                    loading={loading === "submit"}
                  >
                    Submit
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>

      <PersistentFooter />
    </Layout>
  );
};

export default DailySalesReport;
